import React, { useCallback, useEffect, useState, useRef } from "react";
import { faImage, faTrash, faX } from "@fortawesome/free-solid-svg-icons";
import { Form } from "react-bootstrap";
import moment from "moment";
import {
  Box,
  Breadcrumbs,
  makeStyles,
  Modal,
  Typography,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import { RootState, Tag, useAppDispatch } from "../../redux";
import { Card, ReportMenu } from "../../components";
import { getBinsUsageData2, ITagUsageData } from "../../api";
import debounce from "lodash.debounce";
import { callAPI } from "../../api/callAPI";
import {
  GridComponent,
  ColumnDirective,
  ColumnsDirective,
  Page,
  Inject,
  Filter,
  Toolbar,
  PdfExport,
  ExcelExport,
  Sort,
} from "@syncfusion/ej2-react-grids";
import DateFnsUtils from "@date-io/date-fns";
import {
  MuiPickersUtilsProvider,
  KeyboardTimePicker,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import Color from "color";
import { PdfTrueTypeFont } from "@syncfusion/ej2-pdf-export";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GeoTaggedImage from "../../components/GeotaggedImage/GeoTaggedImage";

// --- STYLES MOVED OUTSIDE COMPONENT ---
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "54vw",
  bgcolor: "background.paper",
  borderRadius: "10px",
  p: 2,
  height: "70vh",
};

const useStyles = makeStyles({
  root: {
    border: 2,
    borderRadius: 3,
    color: "white",
    height: 48,
    justifyContent: "center",
    margin: 20,
    textAlign: "center",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    border: "none",
    boxShadow: "5px 5px 5px 5px rgba(19, 8, 173, 0.3)",
    backgroundColor: "white",
    height: 200,
    width: 400,
    textAlign: "center",
    padding: 10,
  },
  textField: {
    backgroundColor: "white",
    marginLeft: "10px",
    border: "1px solid blue",
    width: "140px",
  },
});

interface IBinsUsageData extends Tag, ITagUsageData {}

const DataGridComponent: React.FC<{
  startDate: Date | null;
  endDate: Date | null;
  setStartDate: (s: Date | null) => void;
  setEndDate: (s: Date | null) => void;
  rowData: any;
  Loading: boolean;
  SearchQuery: string;
  handleSearchQuery: (query: string) => void;
}> = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  rowData,
  Loading,
  SearchQuery,
  handleSearchQuery,
}) => {
  const [open, setOpen] = useState({
    modal: false,
    data: null,
    location: null,
    segregatted: false,
  });
  const handleOpen = (data: any, location?: any, segregatted = false) =>
    setOpen({ modal: true, data, location, segregatted });
  const handleCloseModal = () =>
    setOpen({ modal: false, data: null, location: null, segregatted: false });

  const classes = useStyles();
  const [ReportEl, setReportEl] = useState<null | HTMLElement>(null);

  // Use useRef for Grid instance
  const gridRef = useRef<GridComponent | null>(null);

  const [cleanCount, setCleanCount] = useState(rowData ? rowData.length : 0);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setReportEl(event.currentTarget);
  };

  const handleClose = () => {
    setReportEl(null);
  };

  const filterOption = {
    ignoreAccent: true,
    type: "Excel",
  };

  const actionComplete = (args: any) => {
    if (args.requestType === "filtering" && gridRef.current) {
      // @ts-ignore
      setCleanCount(gridRef.current.pageSettings.totalRecordsCount);
    }
  };

  const toolbarOptions = ["Search", "PdfExport", "ExcelExport"];
  let adventProFont: string = "AAEAAAARAQAABAAQRFNJRwAAAAEAALa8..."; // (Truncated for brevity, assuming existing font string)

  const toolbarClick = (args: any) => {
    if (gridRef.current) {
      if (args.item.id.includes("pdfexport")) {
        gridRef.current.pdfExport({
          pageSize: "B2",
          fileName: "Bins-Cleaned-Data.pdf",
          theme: {
            header: {
              bold: true,
              fontColor: "#000000",
              fontName: "Calibri",
              fontSize: 14,
            },
            record: {
              fontColor: "#000000",
              fontName: "Calibri",
              fontSize: 12,
              font: new PdfTrueTypeFont(adventProFont, 13),
            },
          },
          header: {
            contents: [
              {
                position: { x: 750, y: 130 },
                style: { textBrushColor: "#F64C72", fontSize: 28 },
                type: "Text",
                value: "Bins Cleaned Data",
              },
              {
                position: { x: 0, y: 130 },
                style: { textBrushColor: "#000000", fontSize: 18 },
                type: "Text",
                value: `Created Date: ${moment().format("DD-MM-YYYY")}`,
              },
              {
                position: { x: 1600, y: 130 },
                style: { textBrushColor: "#000000", fontSize: 18 },
                type: "Text",
                value: `Created Time: ${moment().format("HH:mm:ss")}`,
              },
              // Image config omitted for brevity, keep existing
            ],
            fromTop: 0,
            height: 200,
          },
        });
      } else if (args.item.id.includes("excelexport")) {
        gridRef.current.excelExport();
      }
    }
  };

  useEffect(() => {
    if (rowData) {
      setCleanCount(rowData.length);
    }
  }, [rowData]);

  console.log("Row Data: ", rowData);

  return (
    <>
      <div
        className={classes.root}
        style={{
          height: "80vh",
          width: "100%",
          textAlign: "center",
          color: "white",
          overflow: "scroll",
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" style={{ color: "white" }}>
          <Typography onClick={handleClick}>Reports</Typography>
          <Typography>Cleaned Bins Data Report</Typography>
        </Breadcrumbs>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          open={open.modal}
          onClose={handleCloseModal}
        >
          <Box sx={style}>
            <div>
              <FontAwesomeIcon
                icon={faX}
                style={{
                  color: "red",
                  cursor: "pointer",
                  float: "right",
                  zIndex: 1000,
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "white",
                  borderRadius: "50%",
                  border: "1px solid red",
                  padding: 3,
                  width: 13,
                  height: 13,
                }}
                onClick={handleCloseModal}
              />
            </div>
            {open?.data && (
              <GeoTaggedImage
                images={open?.data}
                location={open?.location}
                segregatted={open?.segregatted}
              />
            )}
          </Box>
        </Modal>
        <ReportMenu
          ReportEl={ReportEl}
          id="report-menu"
          handleClose={handleClose}
          handleClick={() => {}}
        />
        <h3>Cleaned Bins Data Report</h3>
        <Form.Group
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
            paddingTop: 20,
          }}
        >
          <div style={{ marginLeft: 10 }}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                className={classes.textField}
                margin="normal"
                id="date-picker-dialog-start"
                label="Starting Date"
                format="dd/MM/yyyy"
                value={startDate}
                onChange={setStartDate}
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
              <KeyboardTimePicker
                className={classes.textField}
                margin="normal"
                id="time-picker-start"
                label="Starting Time"
                value={startDate}
                onChange={setStartDate}
                KeyboardButtonProps={{
                  "aria-label": "change time",
                }}
              />
              <KeyboardDatePicker
                className={classes.textField}
                margin="normal"
                id="date-picker-dialog-end"
                label="Ending Date"
                format="dd/MM/yyyy"
                value={endDate}
                onChange={setEndDate}
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
              <KeyboardTimePicker
                className={classes.textField}
                margin="normal"
                id="time-picker-end"
                label="Ending Time"
                value={endDate}
                onChange={setEndDate}
                KeyboardButtonProps={{
                  "aria-label": "change time",
                }}
              />
            </MuiPickersUtilsProvider>
          </div>
          <div style={{ width: 100, marginLeft: "5%" }}>
            <Card
              Icon={faTrash}
              Value={cleanCount}
              Title="Total Bins Cleaned"
              Description="The total number of deployed cards scanned by the solid waste cleaning agencies."
              FontColor={Color.rgb(255, 255, 255)}
              BackgroundColor={Color("#36bea6", "hex")}
            />
          </div>
        </Form.Group>
        <GridComponent
          dataSource={rowData}
          ref={gridRef}
          height="370"
          width="98%"
          toolbar={toolbarOptions}
          allowPaging={true}
          allowFiltering={true}
          // @ts-ignore
          filterSettings={filterOption}
          toolbarClick={toolbarClick}
          allowSorting={true}
          allowPdfExport={true}
          allowExcelExport={true}
          enableHover={true}
          actionComplete={actionComplete}
          allowTextWrap={true}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="id"
              headerText="Sl No"
              // @ts-ignore
              textAlign="left"
              width="100"
            />
            <ColumnDirective
              field="premise_no"
              headerText="Premise No"
              // @ts-ignore
              textAlign="left"
              width="150"
            />
            <ColumnDirective
              field="plot_no"
              headerText="Plot No"
              // @ts-ignore
              textAlign="left"
              width="130"
            />
            <ColumnDirective
              field="block"
              headerText="Block"
              // @ts-ignore
              textAlign="left"
              width="130"
            />
            <ColumnDirective
              field="action_area"
              headerText="Action Area"
              // @ts-ignore
              textAlign="left"
              width="150"
            />
            <ColumnDirective
              field="application_name"
              headerText="Application Name"
              // @ts-ignore
              textAlign="left"
              width="200"
            />
            <ColumnDirective
              field="project_name"
              headerText="Project Name"
              // @ts-ignore
              textAlign="left"
              width="200"
            />
            <ColumnDirective
              field="latitude"
              headerText="Latitude"
              // @ts-ignore
              textAlign="left"
              width="150"
            />
            <ColumnDirective
              field="longitude"
              headerText="Longitude"
              // @ts-ignore
              textAlign="left"
              width="150"
            />
            <ColumnDirective
              field="date"
              headerText="Date"
              // @ts-ignore
              textAlign="left"
              width="120"
            />
            <ColumnDirective
              field="time"
              headerText="Time"
              // @ts-ignore
              textAlign="left"
              width="120"
            />
            <ColumnDirective
              field="is_segregated"
              headerText="Segregated"
              // @ts-ignore
              textAlign="left"
              width="150"
              template={(args: any) => {
                return (
                  <div
                    style={{
                      background:
                        args?.is_segregated === "false" ||
                        args?.is_segregated === "null"
                          ? "red"
                          : "green",
                      padding: "3px 12px",
                      borderRadius: 20,
                      color: "white",
                      width: "fit-content",
                    }}
                  >
                    {args?.is_segregated === "false" ||
                    args?.is_segregated === "null"
                      ? "No"
                      : "Yes"}
                  </div>
                );
              }}
            />
            <ColumnDirective
              field="photo_url"
              headerText="Photo URL"
              // @ts-ignore
              textAlign="left"
              width="150"
              template={(args: any) => {
                const isSegregated =
                  !(args?.is_segregated === "false" || args?.is_segregated === "null");
                return (
                  <>
                    {args?.photo !== "null" && (
                      <button
                        onClick={() =>
                          handleOpen(
                            args?.photo,
                            {
                              latitude: args?.latitude,
                              longitude: args?.longitude,
                            },
                            isSegregated
                          )
                        }
                        style={{
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faImage}
                          size="lg"
                          color="#36bea6"
                        />
                      </button>
                    )}
                  </>
                );
              }}
            />
          </ColumnsDirective>
          <Inject
            services={[Page, Filter, Sort, Toolbar, PdfExport, ExcelExport]}
          />
        </GridComponent>
      </div>
    </>
  );
};

export const BinsCleanedDataReport = () => {
  const { tags } = useSelector((state: RootState) => state.tags);
  const { token, refreshToken } = useSelector((state: RootState) => state.user);
  const dispatch = useAppDispatch();
  const [LiveBinsData, setLiveBinsData] = useState<IBinsUsageData[]>([]);

  const [startDateTime, setStartDateTime] = useState<Date | null>(
    new Date(`${moment().format().split("T")[0]}T06:00:00+05:30`),
  );
  const [endDateTime, setEndDateTime] = useState<Date | null>(
    new Date(moment().format()),
  );
  const setStartDatesTime = (s: Date | null) => {
    setStartDateTime(s);
  };
  const setEndDatesTime = (e: Date | null) => {
    setEndDateTime(e);
  };
  const [FilteredData, setFilteredData] = useState<IBinsUsageData[]>([]);

  const GetBinsUsageData = useCallback(
    async (startDate, endDate) => {
      // Prevent first-load empty requests while auth state is still being rehydrated.
      if (!token || !refreshToken) return;
      const result = await callAPI<any>(
        getBinsUsageData2,
        { token: token, startDate: startDate, endDate: endDate },
        refreshToken,
        dispatch,
      );

      // Add safety check for result data
      if (!result?.data?.tags) {
        setLiveBinsData([]);
        setFilteredData([]);
        return;
      }

      const tagKeys = Object.keys(result.data.tags);

      if (tagKeys.length === 0) {
        setLiveBinsData([]);
        setFilteredData([]);
        return;
      }

      const newData = tagKeys
        .map((k) => {
          const d = result?.data.tags[k].map((item: any) => ({
            ...item,
            id: item.lastCleanedAt.split(" ")[1],
            time: item.lastCleanedAt.split(" ")[1],
            date: k,
          }));
          return d;
        })
        // Added initial value [] to reduce to prevent crash on empty arrays
        .reduce((pre: any, next: any) => [...pre, ...next], [])
        .sort((pre: any, next: any) => {
          return pre.premise_no === next.premise_no
            ? 0
            : pre.premise_no > next.premise_no
              ? 1
              : -1;
        });

      const data: IBinsUsageData[] = newData.map((tag: any) => {
        const usage: any = tags.find(
          (i: any) => i.premise_no === tag.premise_no,
        );

        return {
          ...tag,
          photo: tag?.photo_url,
          // If Redux `tags` hasn't loaded yet, don't clobber API-provided
          // fields like `isCleanedToday` / `lastCleanedAt`.
          ...(usage ? usage : {}),
        };
      });

      const cleanedBins = data.filter((d) => d.isCleanedToday);

      const idData = cleanedBins.map((item: any, index: number) => ({
        ...item,
        id: index + 1,
      }));
      setLiveBinsData(idData);
      setFilteredData(idData);
    },
    [token, refreshToken, dispatch, tags], // Updated dependency array
  );

  const [SearchQuery, setSearchQuery] = useState("");

  const debouncedFilter = useCallback(
    debounce((q: string) => {
      if (q === "") {
        setFilteredData(LiveBinsData);
        return;
      }
      const searcher = new RegExp(q, "i");
      const data = LiveBinsData.filter(
        (i) => JSON.stringify(i).match(searcher) !== null,
      );
      setFilteredData(data);
    }, 1000),
    [LiveBinsData],
  );

  const handleSearchQuery = (query: string) => {
    setSearchQuery(query);
    debouncedFilter(query);
  };

  useEffect(() => {
    if (!token || !refreshToken) return;
    if (!startDateTime || !endDateTime) return;

    GetBinsUsageData(
      moment(startDateTime).format("YYYY-MM-DD HH:mm:ss"),
      moment(endDateTime).format("YYYY-MM-DD HH:mm:ss"),
    );
  }, [startDateTime, endDateTime, GetBinsUsageData, token, refreshToken]);

  return (
    <DataGridComponent
      startDate={startDateTime}
      endDate={endDateTime}
      setStartDate={setStartDatesTime}
      setEndDate={setEndDatesTime}
      rowData={FilteredData}
      Loading={FilteredData.length === 0}
      SearchQuery={SearchQuery}
      handleSearchQuery={handleSearchQuery}
    />
  );
};
